import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

// Services
import { NavigationParam, RouteParam } from '../service-base';
import { TransactionServiceBase } from './abstract/transaction-service.base';

// Modèles
import { BankAccountLine } from 'src/app/@shared/models/banking/bank-account-lines/bank-account-line.model';
import { GeneralFactory } from 'src/app/@shared/models/factories/general-factory';
import { LoanInstalment } from 'src/app/@shared/models/banking/loans/loan-instalment.model';

const CLASS_NAME = 'BankAccountLine';
const ENDPOINT = `/bank-account-lines/`;
const NAVIGATIONS: NavigationParam[] = [
    { key: 'account-entries', path: 'account-entries/', childClass: 'AccountEntryLine' },
    { key: 'find-rule', path: 'find-rule/', childClass: 'BankAccountRule', resultCardinality: 'one' },
    { key: 'matching-entries', path: 'matching-entries/', childClass: 'LedgerEntry' },
    { key: 'potential-settlements', path: 'potential-settlements/', childClass: 'Settlement' },
    { key: 'settlements', path: 'settlements/', childClass: 'Settlement' },
]
const ROUTES: RouteParam[] = [
    { key: 'export', path: 'export/' },
    { key: 'loanInstalment', path: '{id}/loan-instalment/' },
    { key: 'massDelete', path: 'mass-delete/' },
    { key: 'recordOverpayment', path: '{id}/record-overpayment/' },
    { key: 'writeOff', path: '{id}/write-off/' },
]

@Injectable({
    providedIn: 'root'
})
export class BankAccountLineService extends TransactionServiceBase {
    constructor() {
        super(CLASS_NAME, ENDPOINT);
        this.addNavigations(NAVIGATIONS);
        this.addRoutes(ROUTES);
    }

    /**
     * Écarte le signalement de doublon de la ligne : `duplicate` à faux, **et rien d'autre**
     * (#1230).
     *
     * Le serveur refuse toute autre modification d'une ligne signalée
     * (`BANK_ACCOUNT_LINE_DUPLICATE_READ_ONLY`), imputations comprises, même identiques à
     * celles qu'elle porte : envoyer la ligne entière rendrait impossible l'acceptation d'une
     * ligne signalée qui avait été imputée avant que la règle existe. La ligne renvoyée —
     * qualifiée par les règles et la mémoire du dossier dès que le signalement tombe — est
     * diffusée par `updated$`, comme après un enregistrement.
     */
    public acceptDuplicate$(line: BankAccountLine): Observable<BankAccountLine> {
        const url = this.routes.update.replace('{id}', line.id.toString());
        return this._putAction$<BankAccountLine>(url, { duplicate: false }).pipe(
            tap(accepted => this._updated$.next(accepted))
        );
    }

    /**
     * Rapproche la ligne d'une échéance d'emprunt (miroir côté ligne bancaire).
     * Pose le lien 1-1, qualifie la ligne sur l'emprunt et régénère la ventilation.
     * Retourne la ligne mise à jour.
     */
    public matchLoanInstalment$(line: BankAccountLine, instalment: LoanInstalment): Observable<BankAccountLine> {
        let url = this.routes['loanInstalment'].replace('{id}', line.id.toString());
        return this.httpClient.put(url, { loanInstalment: instalment.id }).pipe(
            map((body: any) => GeneralFactory.get('BankAccountLine', body) as BankAccountLine)
        );
    }

    public recordOverpayment$(line: BankAccountLine): Observable<BankAccountLine> {
        return this.update$(line, {
            url: this.routes.recordOverpayment.replace('{id}', line.id.toString())
        });
    }

    /**
     * Dé-rapproche la ligne de son échéance d'emprunt (miroir côté ligne bancaire) :
     * l'échéance repasse pending et la ventilation revient à l'imputation capital par
     * défaut. Retourne la ligne mise à jour.
     */
    public unmatchLoanInstalment$(line: BankAccountLine): Observable<BankAccountLine> {
        let url = this.routes['loanInstalment'].replace('{id}', line.id.toString());
        return this.httpClient.delete(url).pipe(
            map((body: any) => GeneralFactory.get('BankAccountLine', body) as BankAccountLine)
        );
    }

    public writeOff$(line: BankAccountLine): Observable<BankAccountLine> {
        return this.update$(line, {
            url: this.routes.writeOff.replace('{id}', line.id.toString())
        });
    }
}
